import { cn } from '../lib/utils';
import { Badge } from './ui/badge';
import ActivityMap from './ActivityMap';
import { getActivityTypeBadges } from '../utils/activityUtils';
import {
  Sparkles,
  MapPin,
  Clock,
  TrendingUp,
  Heart,
  HeartPulse,
  Flame,
  ChevronRight as ArrowRight,
} from 'lucide-react';

function formatDistance(meters) {
  const m = parseFloat(meters);
  if (!Number.isFinite(m) || m <= 0) return null;
  return `${(m / 1000).toFixed(2)} km`;
}

function formatDuration(seconds) {
  const n = parseFloat(seconds);
  if (!Number.isFinite(n) || n < 0) return null;
  const h = Math.floor(n / 3600);
  const mm = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  if (h > 0) return `${h}:${mm.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${mm}:${s.toString().padStart(2, '0')}`;
}

function formatPace(speed) {
  const sp = parseFloat(speed);
  if (!Number.isFinite(sp) || sp <= 0) return null;
  const paceSeconds = 1000 / sp;
  if (!Number.isFinite(paceSeconds)) return null;
  const mins = Math.floor(paceSeconds / 60);
  const secs = Math.round(paceSeconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')} /km`;
}

function formatLoad(score) {
  const s = parseFloat(score);
  if (!Number.isFinite(s) || s <= 0) return null;
  return String(Math.round(s));
}

function formatAvgHr(hr) {
  const h = parseFloat(hr);
  if (!Number.isFinite(h) || h <= 0) return null;
  return `${Math.round(h)} bpm`;
}

function formatCalories(cal) {
  const c = parseFloat(cal);
  if (!Number.isFinite(c) || c <= 0) return null;
  return `${Math.round(c)} kcal`;
}

function formatActivityDate(dateStr) {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function StatPill({ icon, value }) {
  if (value == null || value === '') return null;
  return (
    <span className='inline-flex items-center gap-1 text-xs text-muted-foreground'>
      {icon}
      <span className='text-foreground font-medium tabular-nums'>{value}</span>
    </span>
  );
}

/**
 * Activity row matching the Recent Activities widget layout (badges, stat pills, optional map).
 * @param {Object} props
 * @param {Object} props.activity - strava_activities row
 * @param {Function} props.t - i18n `t`
 * @param {'featured'|'list'|'card'} [props.surface] - featured = widget first row; list = widget other rows; card = bordered panel (modals)
 * @param {boolean} [props.showLatestBadge]
 * @param {boolean} [props.showMap] - render route map when polyline exists
 * @param {number} [props.mapHeight]
 * @param {function} [props.onCardClick] - whole row clickable (navigate)
 * @param {string} [props.shareImageUrl] - optional session share image (featured row)
 * @param {boolean} [props.showTrailingChevron] - chevron when onCardClick
 * @param {React.ReactNode} [props.actions] - right column (e.g. buttons); stops propagation
 * @param {string} [props.className]
 */
export default function StravaActivityListCard({
  activity,
  t,
  surface = 'card',
  showLatestBadge = false,
  showMap = false,
  mapHeight = 160,
  shareImageUrl = null,
  onCardClick,
  showTrailingChevron = false,
  actions = null,
  className,
}) {
  const hasMap = !!(activity.polyline || activity.summary_polyline);
  const displayMap = showMap && hasMap;
  const { mainLabel, mainColor, extraBadges } = getActivityTypeBadges(activity, t);

  const surfaceClass =
    surface === 'featured'
      ? 'bg-primary/5 border-2 border-primary/30 hover:bg-primary/10 hover:border-primary/50 p-2 -m-2 cursor-pointer'
      : surface === 'list'
        ? 'rounded-lg px-3 py-2.5 hover:bg-accent/55 cursor-pointer transition-colors'
        : 'border border-border bg-card p-2 sm:p-3 hover:border-primary/30';

  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 rounded-lg transition-colors group',
        surfaceClass,
        className
      )}
      onClick={onCardClick}
      onKeyDown={
        onCardClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onCardClick(e);
              }
            }
          : undefined
      }
      role={onCardClick ? 'button' : undefined}
      tabIndex={onCardClick ? 0 : undefined}
    >
      <div className='flex-1 min-w-0'>
        <div className='flex flex-wrap items-center gap-1.5 mb-1'>
          {showLatestBadge && (
            <Badge variant='secondary' className='text-xs shrink-0 gap-0.5 bg-primary/15 text-primary border-primary/30'>
              <Sparkles className='h-3 w-3' />
              {t('activities.latest')}
            </Badge>
          )}
          <p
            className={cn(
              'truncate group-hover:text-primary transition-colors',
              surface === 'featured' ? 'text-sm font-semibold text-foreground' : 'text-sm font-medium text-foreground'
            )}
          >
            {activity.name || t('activityDetail.untitled')}
          </p>
          <Badge variant='outline' className={cn('text-xs shrink-0', mainColor)}>
            {mainLabel}
          </Badge>
          {extraBadges.map((b) => (
            <Badge key={b.label} variant='outline' className={cn('text-xs shrink-0', b.className)}>
              {b.label}
            </Badge>
          ))}
        </div>
        <p className='text-xs text-muted-foreground mb-2'>
          {formatActivityDate(activity.start_date_local || activity.start_date)}
        </p>
        {shareImageUrl && surface === 'featured' && (
          <div className='mt-2 mb-2 rounded-lg overflow-hidden border border-border max-h-40'>
            <img src={shareImageUrl} alt='' className='w-full h-36 object-cover' />
          </div>
        )}
        <div className='flex flex-wrap gap-x-4 gap-y-1'>
          <StatPill icon={<MapPin className='h-3 w-3' />} value={formatDistance(activity.distance)} />
          <StatPill icon={<Clock className='h-3 w-3' />} value={formatDuration(activity.moving_time)} />
          <StatPill icon={<TrendingUp className='h-3 w-3' />} value={formatPace(activity.average_speed)} />
          <StatPill icon={<HeartPulse className='h-3 w-3' />} value={formatLoad(activity.suffer_score)} />
          <StatPill icon={<Heart className='h-3 w-3' />} value={formatAvgHr(activity.average_heartrate)} />
          <StatPill icon={<Flame className='h-3 w-3' />} value={formatCalories(activity.calories)} />
        </div>
        {displayMap && (
          <div className='mt-3 -mx-1 rounded-lg overflow-hidden' onClick={(e) => e.stopPropagation()}>
            <ActivityMap
              encodedPolyline={activity.polyline}
              summaryPolyline={activity.summary_polyline}
              height={mapHeight}
              className='pointer-events-none'
              fitPadding={[5, 5]}
            />
          </div>
        )}
      </div>
      {actions && (
        <div className='flex flex-col gap-1 shrink-0 pt-0.5' onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
      {showTrailingChevron && onCardClick && !actions && (
        <ArrowRight className='h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0' />
      )}
    </div>
  );
}
